/**
 * insightsConfig.js – copy and config for Client Profile insight cards (goals, family/estate).
 * Used by ClientProfile in App.js to render WelcomeInsight cards. Icons from lucide-react.
 */
import type { LucideIcon } from 'lucide-react';
import {
    Flag,
    Home,
    Building,
    Users,
    HeartPulse,
    Wallet,
    FileQuestion,
    ShieldCheck,
    FileText,
    User,
    Gift,
    AlertTriangle,
} from 'lucide-react';
import type { FactFindFamily } from '../../../shared/src/calculation/factFindTypes.ts';

interface GoalInsight {
    title: string;
    text: string;
    icon: LucideIcon;
}

/**
 * "Not in place" for an estate document — NO **or** UNSURE (owner-reported, 2026-08-31).
 *
 * Someone who does not KNOW whether they have a Will, an Enduring Power of Attorney or an Advance
 * Care Directive is in the same practical position as someone who does not have one: they cannot
 * rely on it, and the first person to find out will be their family at the worst possible moment.
 * Treating 'unsure' as a pass hid the insight from exactly the people most likely to need it.
 *
 * The super nomination insight already worked this way (owner-caught, 2026-08-25). These three were
 * left behind; this is the same rule applied consistently.
 */
const notInPlace = (value: unknown): boolean => value === 'no' || value === 'unsure';

export const GOAL_INSIGHTS: Record<string, GoalInsight> = {
    default: {
        title: "Your Goals & Needs",
        text: "Defining your goals and care needs helps us tailor strategies that align with your priorities for lifestyle, finances, and legacy.",
        icon: Flag
    },
    home_care: {
        title: "Support at Home",
        text: "There are eight Support at Home classifications, each with a different government subsidy. Your classification is set through an aged care needs assessment and determines your budget for services such as cleaning, personal care, and nursing.",
        icon: Home
    },
    aged_care: {
        title: "Residential Aged Care",
        text: "Moving into aged care involves significant financial decisions, including how to pay for your accommodation (lump-sum RAD, daily DAP, or a combination) and ongoing fees. These choices have a major impact on your pension and estate.",
        icon: Building
    },
    retirement_village: {
        title: "Retirement Village",
        text: "Retirement villages offer an independent lifestyle with community benefits. The financial contracts can be complex, often involving an entry payment, ongoing fees, and exit fees (Deferred Management Fee) that affect your final return.",
        icon: Users
    },
    granny_flat: {
        title: "Granny Flat Interest",
        text: "A granny flat arrangement involves transferring assets or money to family in exchange for a lifetime right to live in their home. This has specific and complex implications for your pension eligibility and estate planning.",
        icon: HeartPulse
    },
    downsize_home: {
        title: "Downsizing the Home",
        text: "Downsizing is often about moving to a more manageable property. If this frees up capital, it can significantly impact your assets for the Age Pension test. Special 'downsizer' rules may allow you to contribute some sale proceeds to super.",
        icon: Wallet
    },
    acat_assessment: {
        title: "What is an aged care needs assessment?",
        text: "An aged care needs assessment is required to access government-funded aged care services, including Support at Home and, where approved, residential aged care and respite. It is a free assessment, usually completed by an assessment organisation (often in your home). You can get a referral from your GP or apply through My Aged Care. (Formerly known as an ACAT or ACAS assessment.)",
        icon: FileQuestion
    }
};

interface FamilyEstateInsight {
    key: string;
    trigger: (family: FactFindFamily, completer?: string, isPartnered?: boolean) => boolean;
    title: string;
    text: string;
    icon: LucideIcon;
    hasModal?: boolean;
}

export const FAMILY_ESTATE_INSIGHTS: FamilyEstateInsight[] = [
    {
        key: 'default',
        trigger: () => true,
        title: "Protecting Your Family & Legacy",
        text: "Estate planning isn't just about legal documents—it's about protecting your family from stress, disputes, and unnecessary costs. Clear communication and proper planning now ensures your wishes are respected and your family relationships remain strong.",
        icon: ShieldCheck
    },
    {
        key: 'noWill',
        trigger: (family) => notInPlace(family.clientWill) || notInPlace(family.partnerWill),
        title: "What Happens If You Die Without a Will?",
        text: "If you pass away without a valid Will, your assets will be distributed according to state intestacy laws. This may not reflect your wishes and can cause delays, extra costs, and family disputes. It's strongly recommended to have a valid Will in place.",
        icon: FileText
    },
    {
        key: 'noEpoa',
        trigger: (family) => notInPlace(family.clientEpoa) || notInPlace(family.partnerEpoa),
        title: "Why You Need an Enduring Power of Attorney",
        text: "Without an EPOA, your family may need to apply to a tribunal to manage your finances or health decisions if you lose capacity. This process can be lengthy and stressful. Having an EPOA ensures someone you trust can act on your behalf.",
        icon: User
    },
    {
        key: 'noAcd',
        trigger: (family) => notInPlace(family.clientAcd) || notInPlace(family.partnerAcd),
        title: "The Importance of an Advance Care Directive",
        text: "An Advance Care Directive lets you express your wishes for medical care if you can't communicate them yourself. Without one, your family and doctors may face difficult decisions without knowing your preferences.",
        icon: HeartPulse
    },
    {
        key: 'complexFamily',
        trigger: (family) => family.complexDynamics === true,
        title: "Preventing Family Disputes",
        text: "Blended families or estranged relationships require extra care in estate planning. Open, honest conversations about your wishes can prevent costly legal battles and preserve family relationships. Consider involving all relevant family members in discussions about your care and estate plans.",
        icon: Users
    },
    {
        key: 'unevenAssistance',
        trigger: (family) => family.unevenAssistance === true,
        title: "Gifts & Loans to Children",
        text: "Significant gifts or loans to one child can cause disputes after your death. It's important to document these arrangements and consider how they affect your estate plan.",
        icon: Gift
    },
    {
        key: 'noSuperNom',
        // 'unsure' fires it too (owner-caught, 2026-08-25): someone who doesn't KNOW whether a
        // nomination exists needs this explanation at least as much as someone who has none.
        trigger: (family) => ['none', 'unsure', undefined, ''].includes(family.clientSuperNom as string) || ['none', 'unsure', undefined, ''].includes(family.partnerSuperNom as string),
        title: "Securing Your Super for Your Family",
        text: "Your superannuation is often one of your largest assets. Without a binding nomination, the fund trustee decides who receives your super—which may not align with your wishes. A binding nomination ensures your super goes to the right person and provides certainty for your family.",
        icon: Wallet
    },
    {
        key: 'mentalCapacityConcern',
        trigger: (family, completer, isPartnered) => {
            // Written longhand before `notInPlace` existed, and it already had the rule right —
            // this insight is where the no-or-unsure reasoning came from. Now it shares one copy.
            const clientMissingWillOrEpoa = notInPlace(family.clientWill) || notInPlace(family.clientEpoa);
            const partnerMissingWillOrEpoa =
                !!isPartnered && (notInPlace(family.partnerWill) || notInPlace(family.partnerEpoa));
            return clientMissingWillOrEpoa || partnerMissingWillOrEpoa;
        },
        title: "What If Mental Capacity Is Lost Without a Will or EPOA?",
        text: "If your loved one has already lost mental capacity and doesn't have a Will or Enduring Power of Attorney, the process to manage their affairs becomes more complex. The process varies by state and typically involves applying to a tribunal for guardianship or administration orders.",
        icon: AlertTriangle,
        hasModal: true
    }
];

/** Shown wherever a projected cashflow deficit is explained (Aged Care, Support at Home, Retirement Village insight panels). */
export const DEFICIT_CONTEXT_FRAMING_COPY =
    `A cashflow deficit just means that in a given year, you're spending more than you're bringing in — which is normal once fees and everyday living costs are included. ` +
    `It's usually manageable as long as you have savings to draw on to cover the gap. ` +
    `It's worth a closer look if those savings start running low, or if you're leaning on borrowing that your income couldn't comfortably repay.`;

/** First projection year label used in insight panels (aligns with strategy comparison / Yearly Projections). */
export const INSIGHT_COMPARISON_YR1_LABEL = 'Comparison Yr1';

/** Suggestion Engine: dollar band below best projected estate for "Lower RAD, near-best legacy" card (Option A). */
export const SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD = 5000;

/** Badge / recommendation label for the optional near-best-legacy, lower-RAD strategy card. */
export const LOWER_RAD_NEAR_BEST_LEGACY_BADGE = 'Lower RAD, near-best legacy';

/** Tier 1 insight: net worth can grow while annual cashflow is negative. */
export const CASHFLOW_TIER1_NET_WORTH_NOTE =
    `Even in a year where your cashflow is negative, your overall wealth can still grow — for example, if your investments or home rise in value by more than the amount you draw down. These are estimates based on the assumptions in this plan.`;

/** Tier 1 insight: non-advice illustrative disclaimer. */
export const CASHFLOW_TIER1_ILLUSTRATIVE_NOTE =
    'These figures are illustrative only. Outcomes depend on timing, investment returns, fees, Centrelink reassessments, and legislative or policy changes.';
